import { useLang, buildWhatsAppUrl } from "../i18n";
import { useReveal } from "../hooks/useReveal";
import { ArrowUpRight } from "lucide-react";

const IMGS = {
  morph: "https://customer-assets.emergentagent.com/job_1ed3182d-c2e3-4132-9c29-53360c74ba18/artifacts/r0w9bsr6_30bcea32-3803-42b3-98bf-9b81691372f2.jpeg",
  lace: "https://customer-assets.emergentagent.com/job_1ed3182d-c2e3-4132-9c29-53360c74ba18/artifacts/k6tn9my2_386af907-08c1-421b-b1d5-abaa978d8193.jpeg",
  joker: "https://customer-assets.emergentagent.com/job_1ed3182d-c2e3-4132-9c29-53360c74ba18/artifacts/1z6xkhan_29743f16-cfe1-40b9-822c-db0c4efc33a0.jpeg",
  feather: "https://customer-assets.emergentagent.com/job_1ed3182d-c2e3-4132-9c29-53360c74ba18/artifacts/jesfjjwl_55de5c5d-6faf-4536-afbd-819ee473e62b.jpeg",
  elephant: "https://customer-assets.emergentagent.com/job_1ed3182d-c2e3-4132-9c29-53360c74ba18/artifacts/yipyiclg_b024a29c-28a8-4cfb-80ba-dbdc7fabf21f.jpeg",
};

export default function Portfolio() {
  const { t } = useLang();
  const r = useReveal();

  // Bento layout: 12-col grid
  // Row 1: lace (cols 1-7, big), feather (cols 8-12)
  // Row 2: elephant (cols 1-5), joker (cols 6-8), morph (cols 9-12 tall maybe)
  const items = [
    { key: "lace", img: IMGS.lace, tag: t.portfolio.tags[1], cls: "lg:col-span-7 lg:row-span-2 aspect-[4/5] lg:aspect-auto", accent: "coral" },
    { key: "feather", img: IMGS.feather, tag: t.portfolio.tags[3], cls: "lg:col-span-5 aspect-[4/5]", accent: "mustard" },
    { key: "elephant", img: IMGS.elephant, tag: t.portfolio.tags[4], cls: "lg:col-span-5 aspect-square", accent: "coral" },
    { key: "joker", img: IMGS.joker, tag: t.portfolio.tags[2], cls: "lg:col-span-6 aspect-[4/5]", accent: "mustard" },
    { key: "morph", img: IMGS.morph, tag: t.portfolio.tags[0], cls: "lg:col-span-6 aspect-[4/5]", accent: "coral" },
  ];

  return (
    <section id="work" data-testid="portfolio-section" className="relative bg-black py-24 md:py-40">
      <div className="container-x">
        <div ref={r} className="reveal flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline mb-4">{t.portfolio.overline}</p>
            <h2
              data-testid="portfolio-title"
              className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95]"
            >
              {t.portfolio.title}
            </h2>
            <p className="mt-5 max-w-xl text-white/65 text-base md:text-lg">{t.portfolio.sub}</p>
          </div>
          <a
            data-testid="portfolio-cta"
            href={buildWhatsAppUrl(t.whatsapp_pretext)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost self-start"
          >
            {t.portfolio.cta} <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {items.map((it, i) => (
            <a
              key={it.key}
              data-testid={`portfolio-item-${i}`}
              href={buildWhatsAppUrl(`${t.whatsapp_pretext} (${it.tag})`)}
              target="_blank"
              rel="noopener noreferrer"
              className={`group relative overflow-hidden bg-neutral-900 ${it.cls}`}
            >
              <img
                src={it.img}
                alt={it.tag}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              {/* Accent corner */}
              <span
                className="absolute top-4 left-4 w-2.5 h-2.5"
                style={{ background: it.accent === "coral" ? "hsl(var(--coral))" : "hsl(var(--mustard))" }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">
                    0{i + 1} / 0{items.length}
                  </div>
                  <div className="mt-1 font-display text-xl md:text-3xl uppercase tracking-tighter text-white">
                    {it.tag}
                  </div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <div className="border border-white/40 p-2 backdrop-blur-sm">
                    <ArrowUpRight className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
